import React, { useState, useEffect, useMemo } from 'react';

import { Box, CircularProgress, Typography, Button, IconButton } from '@material-ui/core';
import { Sync, ChevronLeft, ChevronRight } from '@mui/icons-material';
import moment from 'moment';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const calculatePagination = (graphData, currentZoomLevel, page) => {
  const effectiveItemsPerPage = Math.round(30 - currentZoomLevel * 2.4);

  const totalPages = Math.max(1, Math.ceil(graphData.length / effectiveItemsPerPage));

  // Standard pagination calculation - start from the beginning of the array
  const startIndex = page * effectiveItemsPerPage;
  const endIndex = Math.min(startIndex + effectiveItemsPerPage, graphData.length);
  const pageData = graphData.slice(startIndex, endIndex);

  // Calculate the minimum zoom level that would result in a single page
  const minZoomForSinglePage = Math.ceil((30 - Math.ceil(graphData.length)) / 2.4);

  const defaultItemsPerPage = Math.round(30 - 5 * 2.4);
  const fitsOnOnePageByDefault = graphData.length <= defaultItemsPerPage;

  return { fitsOnOnePageByDefault, minZoomForSinglePage, pageData, totalPages };
};

// Process data to add a total field that sums routine, callback, and stat and fill missing dates
const processData = (rawData, startDate, endDate) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    // If no data but we have date range, create empty dataset for the full range
    if (startDate && endDate) {
      return generateEmptyDateRange(startDate, endDate);
    }
    return [];
  }

  // First, map the data to include totals
  const dataWithTotals = rawData.map((item) => ({
    ...item,
    total: (item.routine || 0) + (item.callback || 0) + (item.stat || 0),
  }));

  // If there's only one data point or no data, just return it with filled dates
  if (dataWithTotals.length <= 1 && dataWithTotals.length > 0) {
    // We still need to fill dates if we have at least one data point
    const singleItem = dataWithTotals[0];
    const firstDate = singleItem.date;
    let format;
    let granularity;

    if (firstDate.includes('/')) {
      if (firstDate.split('/').length === 3) {
        format = 'MM/DD/YYYY';
        granularity = 'day';
      } else if (firstDate.split('/').length === 2) {
        format = 'MM/YYYY';
        granularity = 'month';
      }
    } else {
      format = 'YYYY';
      granularity = 'year';
    }

    const dateMap = {};
    dateMap[firstDate] = singleItem;

    const result = [];

    const current = moment(firstDate, format);
    const start = moment(current).subtract(5, granularity);
    const end = moment(current).add(4, granularity);

    const datePointer = moment(start);

    while (datePointer.isSameOrBefore(end, granularity)) {
      let dateKey;

      switch (granularity) {
        case 'day':
          dateKey = datePointer.format('MM/DD/YYYY');
          break;
        case 'month':
          dateKey = datePointer.format('MM/YYYY');
          break;
        case 'year':
          dateKey = datePointer.format('YYYY');
          break;
        default:
          dateKey = datePointer.format('MM/DD/YYYY');
      }

      if (dateMap[dateKey]) {
        result.push(dateMap[dateKey]);
      } else {
        result.push({
          callback: 0,
          date: dateKey,
          routine: 0,
          stat: 0,
          total: 0,
        });
      }

      datePointer.add(1, granularity);
    }

    return result;
  }

  if (dataWithTotals.length === 0) {
    if (startDate && endDate) {
      return generateEmptyDateRange(startDate, endDate);
    }
    return [];
  }

  const sortedData = [...dataWithTotals].sort((a, b) => {
    const firstDate = dataWithTotals[0].date;
    let format;

    if (firstDate.includes('/')) {
      if (firstDate.split('/').length === 3) {
        format = 'MM/DD/YYYY';
      } else if (firstDate.split('/').length === 2) {
        format = 'MM/YYYY';
      }
    } else {
      format = 'YYYY';
    }

    if (format) {
      return moment(a.date, format).valueOf() - moment(b.date, format).valueOf();
    }

    return a.date.localeCompare(b.date);
  });

  const result = [];
  const firstItem = sortedData[0];
  const lastItem = sortedData[sortedData.length - 1];

  const firstDate = firstItem.date;
  let format;
  let granularity;

  if (firstDate.includes('/')) {
    // Format like MM/DD/YYYY or MM/YYYY
    if (firstDate.split('/').length === 3) {
      format = 'MM/DD/YYYY';
      granularity = 'day';
    } else if (firstDate.split('/').length === 2) {
      format = 'MM/YYYY';
      granularity = 'month';
    }
  } else {
    format = 'YYYY';
    granularity = 'year';
  }

  const dateMap = {};
  sortedData.forEach((item) => {
    dateMap[item.date] = item;
  });

  let current;
  let end;

  // For other formats (day, month, year)
  if (startDate && endDate) {
    // Convert API format (MM-DD-YYYY) to display format if needed
    const formatStartDate = startDate.includes('-') ? moment(startDate, 'MM-DD-YYYY').format(format) : startDate;
    const formatEndDate = endDate.includes('-') ? moment(endDate, 'MM-DD-YYYY').format(format) : endDate;

    current = moment(formatStartDate, format);
    end = moment(formatEndDate, format);
  } else {
    // Fall back to original behavior if no date range provided
    current = moment(firstDate, format).subtract(5, granularity);
    end = moment(lastItem.date, format);
  }

  while (current.isSameOrBefore(end, granularity)) {
    let dateKey;

    switch (granularity) {
      case 'day':
        dateKey = current.format('MM/DD/YYYY');
        break;
      case 'month':
        dateKey = current.format('MM/YYYY');
        break;
      case 'year':
        dateKey = current.format('YYYY');
        break;
      default:
        dateKey = current.format('MM/DD/YYYY');
    }

    if (dateMap[dateKey]) {
      result.push(dateMap[dateKey]);
    } else {
      result.push({
        callback: 0,
        date: dateKey,
        routine: 0,
        stat: 0,
        total: 0,
      });
    }

    current.add(1, granularity);
  }

  return result;
};

// Helper function to generate empty date range when no data is available
const generateEmptyDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];

  let format;
  let granularity;

  if (startDate.includes('-') && startDate.split('-').length === 3) {
    format = 'MM-DD-YYYY';
    granularity = 'day';
  } else if (startDate.includes('/')) {
    if (startDate.split('/').length === 3) {
      format = 'MM/DD/YYYY';
      granularity = 'day';
    } else if (startDate.split('/').length === 2) {
      format = 'MM/YYYY';
      granularity = 'month';
    }
  } else {
    format = 'YYYY';
    granularity = 'year';
  }

  const result = [];

  const current = moment(startDate, format);
  const end = moment(endDate, format);

  while (current.isSameOrBefore(end, granularity)) {
    let dateKey;

    switch (granularity) {
      case 'day':
        dateKey = current.format('MM/DD/YYYY');
        break;
      case 'month':
        dateKey = current.format('MM/YYYY');
        break;
      case 'year':
        dateKey = current.format('YYYY');
        break;
      default:
        dateKey = current.format('MM/DD/YYYY');
    }

    result.push({
      callback: 0,
      date: dateKey,
      routine: 0,
      stat: 0,
      total: 0,
    });

    current.add(1, granularity);
  }

  return result;
};

const TechnologistObservationChart = ({ data, loading, error, onRefresh, startDate, endDate }) => {
  const [zoomLevel, setZoomLevel] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);

  const processedData = useMemo(() => {
    if (!data) return [];
    return processData(data, startDate, endDate);
  }, [data, startDate, endDate]);

  const { pageData, totalPages, fitsOnOnePageByDefault, minZoomForSinglePage } = useMemo(() => {
    return calculatePagination(processedData, zoomLevel, currentPage);
  }, [processedData, zoomLevel, currentPage]);
  useEffect(() => {
    if (processedData.length > 0) {
      const effectiveItemsPerPage = Math.round(30 - zoomLevel * 2.4);
      const calculatedTotalPages = Math.max(1, Math.ceil(processedData.length / effectiveItemsPerPage));
      setCurrentPage(calculatedTotalPages - 1); // Set to the last page
    } else {
      setCurrentPage(0);
    }
  }, [processedData, zoomLevel]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data || error || (Array.isArray(data) && data.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px" flexDirection="column">
        <Typography variant="body1" color="error" gutterBottom>
          {error || 'No observation data available for the selected time period'}
        </Typography>
        <Button variant="outlined" color="primary" startIcon={<Sync />} onClick={onRefresh} style={{ marginTop: 16 }}>
          Refresh
        </Button>
      </Box>
    );
  }

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" gridGap={24} alignItems="center" mt={2} mb={1}>
        {!fitsOnOnePageByDefault && (
          <div style={{ alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: '20px', display: 'flex', padding: '4px', width: '120px' }}>
            <IconButton
              color="primary"
              onClick={() => {
                const minZoom = Math.max(1, minZoomForSinglePage);
                const newZoom = Math.max(minZoom, zoomLevel - 1);
                setZoomLevel(newZoom);
              }}
              disabled={zoomLevel <= 1 || zoomLevel <= minZoomForSinglePage}
              size="small"
              style={{
                backgroundColor: zoomLevel <= 1 || zoomLevel <= minZoomForSinglePage ? '#f5f5f5' : '#fff',
                borderRadius: '50%',
                padding: '4px 8px',
              }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>-</span>
            </IconButton>

            <Typography variant="body2" style={{ flex: 1, margin: '0 10px', textAlign: 'center' }}>
              Zoom
            </Typography>

            <IconButton
              color="primary"
              onClick={() => {
                const newZoom = Math.min(10, zoomLevel + 1);
                setZoomLevel(newZoom);
                setCurrentPage(0);
              }}
              disabled={zoomLevel >= 10}
              size="small"
              style={{
                backgroundColor: zoomLevel >= 10 ? '#f5f5f5' : '#fff',
                borderRadius: '50%',
                padding: '4px 8px',
              }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
            </IconButton>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: '20px', display: 'flex', padding: '4px' }}>
            <IconButton
              color="primary"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              size="small"
              style={{ backgroundColor: currentPage === 0 ? '#f5f5f5' : '#fff', borderRadius: '50%' }}>
              <ChevronLeft />
            </IconButton>

            <Typography variant="body2" style={{ margin: '0 10px' }}>
              {currentPage + 1}/{totalPages}
            </Typography>

            <IconButton
              color="primary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              size="small"
              style={{ backgroundColor: currentPage === totalPages - 1 ? '#f5f5f5' : '#fff', borderRadius: '50%' }}>
              <ChevronRight />
            </IconButton>
          </div>
        )}
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={pageData} margin={{ bottom: 20, left: 20, right: 30, top: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" strokeOpacity={0.4} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={{ stroke: '#E0E0E0' }}
            interval={0}
            fontSize={Math.min(16, 9 + zoomLevel)}
            angle={Math.round(30 - zoomLevel * 2.4) > 14 ? -45 : 0}
            textAnchor={Math.round(30 - zoomLevel * 2.4) > 14 ? 'end' : 'middle'}
            height={Math.round(30 - zoomLevel * 2.4) > 14 ? 80 : 30}
          />
          <YAxis
            label={{
              angle: -90,
              position: 'insideLeft',
              style: { fontFamily: 'Roboto', fontSize: '14px', textAnchor: 'middle' },
              value: 'Number of negative issues reported',
            }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} formatter={(value, name) => [value, name === 'total' ? 'Total' : name.charAt(0).toUpperCase() + name.slice(1)]} />
          <Legend align="center" verticalAlign="bottom" wrapperStyle={{ padding: '10px' }} />

          {/* Single line showing the total of all observation types */}
          <Line
            type="linear"
            dataKey="total"
            name="Total Observations"
            stroke="#2196F3"
            strokeWidth={2}
            dot={{ fill: '#2196F3', r: 4, strokeWidth: 1 }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />

          {/* Original lines commented out as requested */}
          {/* 
          <Line 
            type="monotone" 
            dataKey="routine" 
            name="Routine" 
            stroke="#4CAF50" 
            strokeWidth={2}
            dot={{ fill: '#4CAF50', r: 4, strokeWidth: 1 }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
          <Line 
            type="monotone" 
            dataKey="callback" 
            name="Callback" 
            stroke="#FF9800" 
            strokeWidth={2}
            dot={{ fill: '#FF9800', r: 4, strokeWidth: 1 }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
          <Line 
            type="monotone" 
            dataKey="stat" 
            name="STAT" 
            stroke="#F44336" 
            strokeWidth={2}
            dot={{ fill: '#F44336', r: 4, strokeWidth: 1 }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
          */}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default TechnologistObservationChart;
